import React, { useState } from 'react';
import { Star, Send, MessageSquare, ThumbsUp, Award } from 'lucide-react';

export default function Feedback() {
  const [feedback, setFeedback] = useState({
    name: '',
    email: '',
    rating: 0,
    category: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFeedback({
      ...feedback,
      [e.target.name]: e.target.value,
    });
  };

  const handleRatingClick = (rating: number) => {
    setFeedback({
      ...feedback,
      rating,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the feedback to your backend
    console.log('Feedback submitted:', feedback);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-green-900/20 to-green-800/20 border border-green-500/20 rounded-xl p-12">
            <ThumbsUp className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
            <p className="text-gray-300 mb-6">
              Your feedback has been submitted successfully. We truly appreciate you taking the time to share your thoughts with us.
            </p>
            <p className="text-gray-400 mb-8">
              Our team will review your feedback and get back to you within 24-48 hours if a response is needed.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFeedback({
                  name: '',
                  email: '',
                  rating: 0,
                  category: '',
                  subject: '',
                  message: '',
                });
              }}
              className="bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-black px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
            >
              Submit Another Feedback
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <MessageSquare className="w-16 h-16 text-gold-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-white mb-4">We Value Your Feedback</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Your opinions help us improve AKIDO™ and provide better fragrance experiences. 
            Share your thoughts, suggestions, or concerns with us.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feedback Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gold-500/20">
              <h2 className="text-2xl font-bold text-white mb-6">Share Your Experience</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={feedback.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={feedback.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Overall Rating *
                  </label>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingClick(star)}
                        className={`w-8 h-8 ${
                          star <= feedback.rating
                            ? 'text-gold-500 fill-current'
                            : 'text-gray-600'
                        } hover:text-gold-400 transition-colors`}
                      >
                        <Star className="w-full h-full" />
                      </button>
                    ))}
                    <span className="ml-2 text-gray-400">
                      {feedback.rating > 0 && (
                        <span>
                          {feedback.rating === 1 && 'Poor'}
                          {feedback.rating === 2 && 'Fair'}
                          {feedback.rating === 3 && 'Good'}
                          {feedback.rating === 4 && 'Very Good'}
                          {feedback.rating === 5 && 'Excellent'}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Feedback Category *
                  </label>
                  <select
                    name="category"
                    value={feedback.category}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold-500"
                  >
                    <option value="">Select Category</option>
                    <option value="product-quality">Product Quality</option>
                    <option value="customer-service">Customer Service</option>
                    <option value="website-experience">Website Experience</option>
                    <option value="shipping-delivery">Shipping & Delivery</option>
                    <option value="pricing">Pricing</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="complaint">Complaint</option>
                    <option value="general">General Feedback</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={feedback.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="Brief description of your feedback"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    value={feedback.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    placeholder="Please share your detailed feedback, suggestions, or concerns..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold-500 resize-vertical"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-black py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>Submit Feedback</span>
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border border-gold-500/20">
              <h3 className="text-lg font-bold text-white mb-4">Why Your Feedback Matters</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-300">
                      <strong className="text-white">Improve Quality:</strong> Your insights help us enhance our products and services.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MessageSquare className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-300">
                      <strong className="text-white">Better Experience:</strong> We use your feedback to create better shopping experiences.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <ThumbsUp className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-300">
                      <strong className="text-white">Community Impact:</strong> Your voice helps other fragrance enthusiasts.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gold-900/20 to-gold-800/20 border border-gold-500/20 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Quick Contact</h3>
              <div className="space-y-3 text-sm">
                <p className="text-gray-300">
                  <strong className="text-gold-400">Email:</strong> akido.help@akido.in
                </p>
                <p className="text-gray-300">
                  <strong className="text-gold-400">Phone:</strong>WACXHIYI LANATH
                </p>
                <p className="text-gray-300">
                  <strong className="text-gold-400">Response Time:</strong> Within 24-48 hours
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border border-gold-500/20">
              <h3 className="text-lg font-bold text-white mb-4">Recent Reviews</h3>
              <div className="space-y-4">
                <div className="border-l-2 border-gold-500 pl-3">
                  <div className="flex items-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-gold-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">"Amazing fragrance collection!"</p>
                  <p className="text-xs text-gray-500">- Customer from Mumbai</p>
                </div>
                
                <div className="border-l-2 border-gold-500 pl-3">
                  <div className="flex items-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-gold-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">"Excellent customer service!"</p>
                  <p className="text-xs text-gray-500">- Customer from Delhi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}